import type { PresenceSlot, Team } from './domain.js';

// Day-board coverage model (S4-core — parent brief §5.4, operator decisions
// D1–D5 2026-07-17). Pure logic: the SPAs read the board tables under RLS and
// compute client-side; the S4-alerts job (next increment) reuses the same
// function server-side. Nothing here touches PostgREST.

/** What makes an activity "in demand" on a given day (D2 mapping, metrics v2 0038). */
export type DemandDriver =
  | 'always'
  | 'departures'
  | 'arrivals'
  /** Recouches — guests already arrived staying overnight. */
  | 'stayovers'
  | 'breakfasts'
  /** Couverts — people having dinner (demi-pension AND walk-ins; no lunch service). */
  | 'covers';

/**
 * Base day metrics a demand formula can weigh, in the operator's display
 * order: hotel (rooms) first, then restaurant (meals). `stayovers`,
 * `occupancy` and `covers` are derived on save, but they are still readable
 * inputs to a formula — a rule may legitimately weigh a derived figure
 * (« la plonge, c'est les couverts »).
 */
export const DEMAND_METRICS = [
  'arrivals',
  'stayovers',
  'departures',
  'occupancy',
  'breakfasts',
  'packedLunches',
  'halfBoards',
  'dinersHotel',
  'diners',
  'covers',
] as const;

export type DemandMetric = (typeof DEMAND_METRICS)[number];

/**
 * French labels for the metrics, shared by the demand form and the formula
 * editor.
 *
 * « à blancs » hangs off `departures`, NOT `arrivals` (F12 / GLOSSARY §7 O1,
 * operator 2026-07-28). An « à blanc » is a *cleaning level triggered by a
 * departure* — the guest left, the room is stripped and cleaned in full. An
 * « arrivée » is a different quantity: which rooms must be READY. The two
 * track each other on a typical day, which is why the old label conflated
 * them, but they diverge exactly when it matters — a departure with no
 * same-day arrival still needs the heavy clean.
 */
export const DEMAND_METRIC_LABELS: Record<DemandMetric, string> = {
  arrivals: 'Arrivées',
  stayovers: 'Recouches',
  departures: 'Départs (à blancs)',
  occupancy: 'Occupation',
  breakfasts: 'Petits-déjeuners',
  packedLunches: 'Paniers repas',
  halfBoards: 'Demi-pensions',
  dinersHotel: 'Dîners',
  // Renamed 2026-09-09: it always meant walk-ins / phone / LS online, and the
  // bare « Dîners » is now the hotel's non-DP diners beside it.
  diners: 'Dîners Ext',
  covers: 'Couverts',
};

/**
 * A weighted sum over base metrics — « le ménage, c'est les à-blancs plus la
 * moitié des recouches » is `{ departures: 1, stayovers: 0.5 }` (the à-blanc
 * count is `departures`, F12; it was `arrivals` until 2026-07-28).
 *
 * Structured rather than an expression string on purpose: every rule the
 * operator needs is a linear combination, and this form cannot be malformed,
 * needs no parser, and evaluates identically in the browser and in the 18:00
 * gap job. Absent metric = coefficient 0.
 */
export type DemandFormula = Partial<Record<DemandMetric, number>>;

export interface BoardActivity {
  id: number;
  label: string;
  /** Default daily window, 'HH:MM' — per-day flex goes through overrides (D1). */
  windowStart: string;
  windowEnd: string;
  /** Display-only nuance (« début 14 h–16 h selon les arrivées »). */
  windowNote: string | null;
  demandDriver: DemandDriver;
  /**
   * Weighted sum over base metrics. When present it is authoritative and
   * `demandDriver` is only consulted for the 'always' anchors. Null falls back
   * to the single-metric driver (pre-v3 rows; the migration backfills every
   * existing activity, so this is a safety net rather than a live path).
   */
  demandFormula: DemandFormula | null;
  /**
   * Minimum driver value before the activity warrants a dedicated person
   * (« la Plonge en dessous de 20 couverts, le serveur s'en occupe »).
   * null = 1, i.e. required as soon as there is any demand. Meaningless for
   * 'always' anchors, whose driver value is a constant 1 — ignored there.
   */
  minDemand: number | null;
  active: boolean;
  sort: number;
  /** Display grouping for the day Gantt (0034) — presentation only, coverage is qualification-driven. */
  team: Team | null;
  /**
   * No time window — a sign-up responsibility (« Téléphone »), not a slot.
   * Rendered as a chip row AFTER the day Gantt, never a bar inside it; its
   * windowStart/windowEnd are an unused 00:00 placeholder (so it contributes
   * no thirds to the presence matrix and never gaps on the hour axis).
   */
  untimed: boolean;
  /**
   * The metric this activity's demand is read from (ADR-0015), and what
   * `minDemand` thresholds. Null falls back to `demandFormula` / `demandDriver`
   * — the legacy path, kept because silently zeroing an activity's demand would
   * turn off a gap alert with nobody watching.
   */
  loadMetricKey?: string | null;
  /**
   * The metric shown on the Gantt bar, with the registry's FR label and unit.
   * Deliberately a SEPARATE reference from `loadMetricKey`: the gate wants a
   * threshold-comparable figure, the badge wants one a human can picture
   * (« 7,3 à-blancs éq. » rather than a dimensionless « 11 »).
   */
  badgeMetricKey?: string | null;
}

export interface ActivityQualification {
  activityId: number;
  employeeId: string;
  /**
   * « Activité principale » — what this person NORMALLY does, a subset of what
   * they CAN do. Optional on the wire: absent from a row read before the
   * is_main migration applied.
   */
  isMain?: boolean;
}


/** Expected load for one day — desk gesture or bulk import (D3, metrics v2 0038). */
export interface BoardDay {
  date: string;
  departures: number;
  arrivals: number;
  /** Recouches — guests already arrived staying overnight. Derived + stored (0041): occupancy − arrivals. */
  stayovers: number;
  /** Rooms occupied that night (running chain, 0041). Null = the chain isn't seeded for this day. */
  occupancy: number | null;
  breakfasts: number;
  /** Paniers repas — « Panier midi » in the effectif (0146). */
  packedLunches: number;
  /** DP — contracted demi-pension covers, from the RESTAURANT booking (0146).
   *  The chef's sure number: he builds a menu from it and extrapolates the rest
   *  off occupancy, which is why it must not include a room-only guest who
   *  happens to have booked a table. */
  halfBoards: number;
  /** Hotel guests dining OUTSIDE demi-pension — « Chbre » / « ChPdj » (0146).
   *  Includes a guest who booked only a table and so has no effectif row. */
  dinersHotel: number;
  /** « Dîners Ext » — walk-ins / phone / LS online, typed by the DESK. The one
   *  surviving manual input. Vega can supply a floor for it
   *  (`dinersExternalBooked`) but not a count: the staff work from a printout
   *  and mostly do not enter walk-ins. */
  diners: number;
  /** Externals who BOOKED, from Vega. A FLOOR shown beside `diners` as a hint,
   *  never a replacement for it (Q7 = A, 2026-09-09). */
  dinersExternalBooked: number;
  /** Couverts au dîner — DERIVED + stored: halfBoards + dinersHotel + diners. */
  covers: number;
  note: string | null;
  updatedAt: string;

  // ── The people dimension (0129, M3) ──────────────────────────────────────
  //
  // NULL means no feed has written this day — never "zero guests". The room
  // counts above are `not null default 0` because a human typed them; these are
  // feed-owned and nothing writes them until M4, so a default of 0 would have
  // every day since 2024 reporting an empty hotel with total confidence.
  //
  // `guests` (adults + children) and `stayoversGuests` are DERIVED and
  // deliberately absent — §5: "guests and stayovers_guests are derived, never
  // stored as inputs", the same rule that keeps recouches out of the desk's
  // hands in rooms.
  guestsAdults: number | null;
  guestsChildren: number | null;
  arrivalsGuests: number | null;
  departuresGuests: number | null;
}

/** Per-day activity flex (D1): shifted window and/or required=false waive. */
export interface ActivityOverride {
  id: number;
  activityId: number;
  date: string;
  /** Null = keep the activity default for that bound. */
  windowStart: string | null;
  windowEnd: string | null;
  required: boolean;
  note: string | null;
  updatedAt: string;
}

/**
 * Slot → interval convention (minutes from midnight): the tripartite day
 * (0036) — matin 06–12, après-midi 12–18, soir 18–23. Declarations are
 * deliberately coarse (a 10-second gesture) — any overlap with an activity's
 * effective window counts as coverage; the board informs, it never dictates.
 */
export const SLOT_INTERVALS: Record<Exclude<PresenceSlot, 'absent'>, readonly [number, number]> = {
  matin: [6 * 60, 12 * 60],
  apresMidi: [12 * 60, 18 * 60],
  soir: [18 * 60, 23 * 60],
};

/** 'HH:MM' or Postgres 'HH:MM:SS' → minutes from midnight. */
export function timeToMinutes(time: string): number {
  const [h = 0, m = 0] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * The presence thirds (matin/après-midi/soir) a [windowStart, windowEnd) time
 * window overlaps — the single rule behind "declaring/assigning someone on an
 * activity marks them present for its slots" (message intent, staff@ tap,
 * desk declare). Shared so every write path expands a window identically.
 */
export function slotsOverlappingWindow(windowStart: string, windowEnd: string): Exclude<PresenceSlot, 'absent'>[] {
  const ws = timeToMinutes(windowStart);
  const we = timeToMinutes(windowEnd);
  return (['matin', 'apresMidi', 'soir'] as const).filter((slot) => {
    const [s, e] = SLOT_INTERVALS[slot];
    return ws < e && s < we;
  });
}

/**
 * Expected headcount driver value for a day ('always' → 1; unknown day → 0).
 * 'covers' stands alone — couverts already includes demi-pension diners, so
 * no composite addition (that would double-count; 0038).
 */
export function demandFor(day: BoardDay | null, driver: DemandDriver): number {
  if (driver === 'always') return 1;
  if (!day) return 0;
  switch (driver) {
    case 'departures':
      return day.departures;
    case 'arrivals':
      return day.arrivals;
    case 'stayovers':
      return day.stayovers;
    case 'breakfasts':
      return day.breakfasts;
    case 'covers':
      return day.covers;
  }
}

/** One base metric off a day. Unknown day → 0; unseeded occupancy → 0. */
export function metricValue(day: BoardDay, metric: DemandMetric): number {
  switch (metric) {
    case 'arrivals':
      return day.arrivals;
    case 'stayovers':
      return day.stayovers;
    case 'departures':
      return day.departures;
    case 'occupancy':
      return day.occupancy ?? 0;
    case 'breakfasts':
      return day.breakfasts;
    case 'packedLunches':
      return day.packedLunches;
    case 'halfBoards':
      return day.halfBoards;
    case 'dinersHotel':
      return day.dinersHotel;
    case 'diners':
      return day.diners;
    case 'covers':
      return day.covers;
  }
}

/**
 * Evaluate a weighted-sum formula against a day. Rounded UP: a load of « 0.5
 * recouches » still means someone has to do half a room, so it must not
 * silently floor to no demand. Result is clamped at 0 (a negative coefficient
 * is allowed — « couverts moins demi-pensions » — but negative demand is not).
 */
export function evaluateFormula(day: BoardDay | null, formula: DemandFormula): number {
  if (!day) return 0;
  let total = 0;
  for (const metric of DEMAND_METRICS) {
    const coefficient = formula[metric];
    if (coefficient) total += coefficient * metricValue(day, metric);
  }
  return Math.max(0, Math.ceil(total));
}

/**
 * The demand figure an activity is judged against: 1 for the 'always'
 * anchors, otherwise its formula (or the legacy single-metric driver when no
 * formula is set). This is what the « Seuil » threshold compares to.
 */
export function demandForActivity(day: BoardDay | null, activity: BoardActivity): number {
  if (activity.demandDriver === 'always') return 1;
  if (activity.demandFormula) return evaluateFormula(day, activity.demandFormula);
  return demandFor(day, activity.demandDriver);
}

/**
 * The demand figure, preferring the registry (ADR-0015) and falling back to the
 * legacy per-activity formula.
 *
 * `resolveMetric` is injected rather than imported so this module stays free of
 * the registry's evaluation context (which needs staffing outputs, hence the
 * staffing model — and importing that here would make the two mutually
 * recursive for no benefit).
 *
 * An UNRESOLVABLE metric falls through to the legacy path rather than reading
 * as 0. Zero demand is a positive claim that nothing is needed, and it would
 * silence a gap alert; the old formula is a worse answer than the new one but a
 * far better one than a confident lie.
 */
export function demandForActivityVia(
  day: BoardDay | null,
  activity: BoardActivity,
  resolveMetric: (key: string) => number | null,
): number {
  if (activity.demandDriver === 'always') return 1;
  if (activity.loadMetricKey) {
    const value = resolveMetric(activity.loadMetricKey);
    if (value !== null) return value;
  }
  return demandForActivity(day, activity);
}

/** Human-readable formula, e.g. « Départs (à blancs) + ½ × Recouches ». */
export function formatFormula(formula: DemandFormula): string {
  const terms = DEMAND_METRICS.filter((m) => !!formula[m]).map((m) => {
    const c = formula[m] as number;
    const label = DEMAND_METRIC_LABELS[m];
    if (c === 1) return label;
    if (c === 0.5) return `½ × ${label}`;
    return `${c.toLocaleString('fr-FR')} × ${label}`;
  });
  return terms.length === 0 ? 'Aucune demande' : terms.join(' + ');
}

/**
 * Running occupancy (0041): tonight's rooms = last night − departures +
 * arrivals, floored at 0. A null previous value means the chain isn't seeded
 * yet, so tonight's occupancy is unknown too (the operator types it to seed).
 */
export function nextOccupancy(prevOccupancy: number | null, arrivals: number, departures: number): number | null {
  if (prevOccupancy == null) return null;
  return Math.max(0, prevOccupancy - departures + arrivals);
}

/** Recouches = occupancy − arrivals, floored at 0 — the value written back into board_days.stayovers on save. */
export function recouchesFromOccupancy(occupancy: number, arrivals: number): number {
  return Math.max(0, occupancy - arrivals);
}

/** Next calendar day for an ISO 'YYYY-MM-DD' key (noon dodges DST edges). */
function nextDay(key: string): string {
  const d = new Date(`${key}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A day the chain can be recomputed for: the two facts the desk enters. */
export interface ChainRow {
  date: string;
  arrivals: number;
  departures: number;
}

/** One recomputed night, plus whether the arithmetic went impossible. */
export interface ChainResult {
  date: string;
  occupancy: number;
  stayovers: number;
  /** True when nr came out negative before clamping — nd exceeded last night's rooms. */
  inconsistent: boolean;
}

/**
 * Propagate the occupancy chain forward from a fixed anchor (0041; algorithm
 * locked with the operator 2026-08-01 — see docs/OCCUPANCY-CHAIN.md).
 *
 *     nr(d+1) = no(d) − nd(d+1)        no(d+1) = nr(d+1) + na(d+1)
 *
 * The anchor day itself is NOT rewritten — it is the fact being propagated
 * from. Every day after it is a consequence, so a manually-entered occupancy
 * downstream is deliberately overwritten.
 *
 * STOP RULE (operator decision): walk CONSECUTIVE calendar days and stop at
 * the first date with no `board_days` row — "no data entered yet" is the end
 * of the season. An all-zero saved day does NOT halt the walk (a quiet
 * Tuesday is real data), and a gap can no longer be silently closed over the
 * way iterating the returned rows did.
 *
 * `rows` may be in any order and may contain dates before the anchor; only
 * the contiguous run after it is used.
 */
export function propagateOccupancy(
  seedDate: string,
  seedOccupancy: number,
  rows: readonly ChainRow[],
): ChainResult[] {
  const byDate = new Map(rows.map((r) => [r.date, r]));
  const out: ChainResult[] = [];
  let running = seedOccupancy;
  for (let date = nextDay(seedDate); ; date = nextDay(date)) {
    const row = byDate.get(date);
    if (!row) break; // first missing day ends the chain
    const rawStayovers = running - row.departures;
    const stayovers = Math.max(0, rawStayovers);
    const occupancy = stayovers + row.arrivals;
    out.push({ date, occupancy, stayovers, inconsistent: rawStayovers < 0 });
    running = occupancy;
  }
  return out;
}

export type CoverageStatus = 'waived' | 'noDemand' | 'covered' | 'gap';

export interface ActivityCoverage {
  activity: BoardActivity;
  override: ActivityOverride | null;
  /** Effective window for the day (override ⊕ default), 'HH:MM(:SS)'. */
  windowStart: string;
  windowEnd: string;
  demand: number;
  /**
   * The Gantt badge, already formatted with its unit (ADR-0015) — « 7,3
   * à-blancs éq. ». Null when the activity names no badge metric, the metric
   * could not be resolved, or the registry was not supplied: in every one of
   * those cases the caller falls back to rendering `demand`, which is what the
   * board did before the registry existed.
   */
  badge: { key: string; labelFr: string; text: string; value: number } | null;
  status: CoverageStatus;
  /** Qualified current employees whose declared (or assignment-implied) slot overlaps the window. Excludes anyone in `assigned`. */
  coveredBy: string[];
  /** Current employees explicitly assigned to this activity that day (0039) — cover it regardless of qualification. */
  assigned: string[];
}

export interface DayCoverage {
  date: string;
  day: BoardDay | null;
  /** False = the desk has not entered the load yet — demand-driven statuses are provisional. */
  demandEntered: boolean;
  /** Cleaning load, derived — never stored (parent brief §5.4). */
  roomsToTurn: number;
  gaps: ActivityCoverage[];
  activities: ActivityCoverage[];
}


/** One employee's presence on a day — the three thirds plus the dominating 'absent'. */
export interface DayPresence {
  absent: boolean;
  matin: boolean;
  apresMidi: boolean;
  soir: boolean;
}


/**
 * French label for a declared slot SET (0035/0036): the selected thirds in
 * day order ('Matin + Soir'); 'Absent·e' dominates; '' when nothing is
 * declared. Shared by the staff, reception, and bos surfaces.
 */
export function slotsLabel(slots: PresenceSlot[]): string {
  if (slots.includes('absent')) return 'Absent·e';
  const parts = [];
  if (slots.includes('matin')) parts.push('Matin');
  if (slots.includes('apresMidi')) parts.push('Après-midi');
  if (slots.includes('soir')) parts.push('Soir');
  return parts.join(' + ');
}

/**
 * Payroll/rest semantics of a declared day (0036, operator rule): a FULL
 * work day = 2+ slots declared; a single slot = a half day. Recorded here
 * for the future rest-day accrual increment (parent brief §5.3) — nothing
 * consumes it for payroll yet (payroll stays clock-derived, Q-L).
 */
export function isFullDay(slots: PresenceSlot[]): boolean {
  return slots.filter((s) => s !== 'absent').length >= 2;
}

/** What the broadcast needs to name a gap — a projection of ActivityCoverage. */
export interface GapSummary {
  label: string;
  windowStart: string;
  windowEnd: string;
}

/** '2026-07-17' + 1 → '2026-07-18' (noon anchor dodges DST edges). */
export function addDaysIso(dateIso: string, delta: number): string {
  const d = new Date(`${dateIso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** '2026-07-18' → 'vendredi 18 juillet' (Paris-invariant: the ISO day is already local). */
export function frDayLabel(dateIso: string): string {
  return new Date(`${dateIso}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * The « postes à pourvoir » broadcast body (S4 — D5). Composed server-side
 * from structured gaps, never free-typed by the desk: the desk TRIGGERS a
 * broadcast, the board speaks (site-scoping decision 2026-07-14 — desk
 * hosts post as themselves only from their own staff.* accounts).
 */
export function formatGapBroadcast(dateIso: string, gaps: GapSummary[]): string {
  const lines = gaps.map(
    (g) => `• ${g.label} (${g.windowStart.slice(0, 5)}–${g.windowEnd.slice(0, 5)})`,
  );
  return [
    `📢 Postes à pourvoir ${frDayLabel(dateIso)} :`,
    ...lines,
    'Merci de déclarer votre présence dans l’app (onglet Présence).',
  ].join('\n');
}


/**
 * One line of a service sheet (0147) — dinner or breakfast, one per réservation.
 *
 * Deliberately pre-formatted by the database: `room` is already « Grpe » for a
 * group and « Ext » for a party with no stay, and `stayType` is already
 * « 1/2 P » / « ChPdj » / « Chbre ». Those are Vega's own words on Vega's own
 * screen, and the staff read this sheet beside that screen — inventing a second
 * vocabulary for the same four values is how two people end up meaning
 * different things by « Chbre ».
 */
export interface ServiceListRow {
  service: 'dinner' | 'breakfast';
  /** Guest surname, or the group's name for a group. */
  party: string;
  /**
   * The MAIN room, short-spelled ('110', never Vega's '0110' — 0158/0149), or
   * « Grpe » / « Ext ».
   *
   * « Main » means the lowest room number of the réservation. Vega records no
   * principal room, so it is a deterministic stand-in, not a fact.
   */
  room: string;
  /**
   * Every room of the réservation, lowest first — the whole set, so a party
   * split across two rooms is visible as one. Null for a group (its rooms are
   * the group table's job) and for a party with no stay.
   */
  rooms: string[] | null;
  /** Type de séjour, in Vega's vocabulary. */
  stayType: string;
  /** Null for a party with no stay — a walk-in has no séjour to print. */
  stayFrom: string | null;
  stayTo: string | null;
  /** Couverts for dinner; petits-déjeuners for breakfast. */
  guests: number;
}

/** The rooms that are NOT the main one, for the line printed underneath it. */
export function relatedRooms(row: ServiceListRow): string[] {
  return (row.rooms ?? []).filter((r) => r !== row.room);
}

/**
 * One room of a GROUP on the breakfast sheet (0158) — the detail behind the
 * single « Grpe » line.
 *
 * ⚠ `pdj` and `effectifTotal` come from DIFFERENT sources and may disagree.
 * `pdj` is heads on the meal plan, the only room-grained figure that exists;
 * `effectifTotal` is the measured count for the whole group (`vega_effectif`,
 * which is réservation-grained — 0139). A sheet that silently picked one of
 * them would be the exact defect 0139 was written to close, so both travel and
 * the renderer shows the gap.
 */
export interface ServiceGroupRoom {
  resa: string;
  groupName: string;
  room: string;
  floor: number;
  /** Breakfasts for this room, from the meal PLAN. */
  pdj: number;
  /** Breakfasts MEASURED for the whole group, or null if Vega recorded none. */
  effectifTotal: number | null;
}


/**
 * One room on the housekeeping sheet (0148/0149) — the étage's list, which is
 * room-grained where everything else the board projects is a count.
 *
 * `room` is the SHORT spelling ('101'), normalised in the database: Vega writes
 * '0101' and the PMS '101', and joining them raw silently loses the floor and
 * the make-up (0149).
 */
export interface CleaningRow {
  section: 'ablancs' | 'recouches' | 'arrivees';
  room: string;
  floor: number;
  /** Heads. On « A Blancs » this is the ARRIVING party where the room turns
   *  over — the room is empty by definition, and « make it up for two » is the
   *  useful fact in the morning block. Null when nobody follows them in. */
  guests: number | null;
  /** The room has more than one make-up, so the sheet prints them all and
   *  leaves a slot to write on. A fixed room needs none: nothing to decide. */
  convertible: boolean;
  /** EVERY arrangement the room can be made up in, ordered (0176). One entry =
   *  not convertible; empty = a room the PMS does not know. Replaces the
   *  `beds`/`alt` pair, which could only ever describe two. */
  arrangements: readonly RoomArrangement[];
  /** `Queen` | `King` | null — which double the room holds. A property of the
   *  ROOM, not of any arrangement (0173). */
  doubleBedType: string | null;
  /**
   * Which arrangement the room is STANDING in, by CODE.
   *
   * ⚠ null means NOT KNOWN, never « the default one » — there is no default
   * (0173). The sheet's honest rendering of null is to print them all unmarked.
   */
  currentConfig: string | null;
  /** Which arrangement the ARRIVING guest needs — the one circled on the
   *  sheet. Null when unknown, and the circling is then done by hand. */
  requestedConfig: string | null;
  /** Vacated this morning AND re-let this afternoon. Set on BOTH rows of such
   *  a room (0150): from A Blancs it means « must be finished, not merely
   *  done », from Arrivées « this one is not empty yet ». Underlined in both. */
  turnover: boolean;
}


// ── Bed arrangements on the printed sheets (0157) ──────────────────────────
//
// The « D / 1S / 2S / 3S » coding is the operator's, for a sheet that is read
// at arm's length and written on in pen. It lives here — a pure function over
// bed counts — rather than in SQL, because it is a printed-sheet vocabulary and
// not a fact about a bedroom (ARCHITECTURE.md §1: the TypeScript domain is a
// functional core). Adding a bed type therefore stays what `pms_bed_config` was
// built for: data entry, no deploy.

/**
 * One arrangement a room can take, as the sheet receives it (0176).
 *
 * ⚠ It replaces `Arrangement = 'default' | 'converted'`, and the reason is not
 * tidiness: those two words could only ever name TWO things, and they named
 * them by position in a code — « Double - Twin » — which is precisely what 0173
 * removed. A room that grows a third arrangement is now one data row away.
 */
export interface RoomArrangement {
  code: string;
  doubles: number;
  singles: number;
}

/** Beds, as the database counts them. No `kings`: whether a double is a Queen
 *  or a King is the ROOM's fact (`CleaningRow.doubleBedType`), and in any case
 *  `bedToken` renders both as « D ». */
export interface BedCount {
  doubles: number;
  singles: number;
}

/**
 * Beds → the sheet's coding. Operator, 2026-09-12: *« D for beds for 2 people,
 * 1S for one single, 2S for 2 singles, 3S for three singles »*.
 *
 * A king and a double are both « D »: the coding answers « how do I make this
 * room up », and at that level they are one bed for two. (The distinction is
 * real and lives on the ROOM — `doubleBedType`, 0173 — and is simply not what
 * the étage is being asked. It is also why 0176 could drop `kings` from the bed
 * counts without moving a single character of the printed sheet.) A mixed layout joins its parts — a Triple, one double plus one
 * single, is « D+1S » — because the coding has no single token for it and
 * inventing one would mean teaching the étage a word for one room.
 *
 * Returns '' for no beds at all, which no configuration has (`pms_bed_config`
 * forbids it); the sheet then prints nothing rather than an empty bracket.
 */
export function bedToken(beds: BedCount): string {
  const parts: string[] = [];
  for (let i = 0; i < beds.doubles; i += 1) parts.push('D');
  if (beds.singles > 0) parts.push(`${beds.singles}S`);
  return parts.join('+');
}

/**
 * The two codes a convertible room prints, and which one is circled.
 *
 * ORDER: the arrangement the room is STANDING in comes first (operator,
 * 2026-09-12 — *« when 'current config' is implemented, it should show
 * first »*). Until `current_bed_config` is fed it is unknown, and the fallback
 * is the room's own nominal arrangement first, which is what the sheet has
 * always implied.
 *
 * CIRCLE: the arrangement the ARRIVAL needs — a different question from which
 * one is standing, and the one the étage acts on. Null when unknown, and the
 * sheet then prints both plainly for someone to circle in pen.
 *
 * Returns null for a room with no choice: there is nothing to decide about it,
 * and a sheet that printed a fixed configuration on all forty-two rooms would
 * bury the eleven that matter.
 */
export function arrangementCodes(row: {
  arrangements: readonly RoomArrangement[];
  currentConfig: string | null;
  requestedConfig: string | null;
}): { code: string; which: string; circled: boolean }[] | null {
  if (row.arrangements.length < 2) return null;
  const entries = row.arrangements.map((a) => ({
    code: bedToken(a),
    which: a.code,
    circled: a.code === row.requestedConfig,
  }));
  // The STANDING arrangement first. Where it is unknown the order is whatever
  // the database gave (alphabetical by code, 0176) — deliberately arbitrary,
  // because « nominal first » was the default and the default is gone.
  const standing = entries.findIndex((e) => e.which === row.currentConfig);
  if (standing > 0) entries.unshift(entries.splice(standing, 1)[0]!);
  return entries;
}

/**
 * Whether the head count is worth printing.
 *
 * Operator, 2026-09-12: *« the default for number of people in a room "a blanc"
 * is 2. So, only show for arrivals the number of people when it's not 2 »*. A
 * room is made up for two unless told otherwise, so printing « (2) » forty
 * times a morning is forty things to read past to find the « (1) » that changes
 * the work.
 *
 * Applies uniformly across all three blocks. A recouche's count is who is
 * already in the room rather than who is arriving, but it is still the same
 * default of two — operator, 2026-09-14: only print the count when it is not
 * two, everywhere.
 */
export const ARRIVAL_DEFAULT_HEADS = 2;

export function showArrivalHeads(
  section: CleaningRow['section'],
  guests: number | null,
): boolean {
  if (guests == null) return false;
  return guests !== ARRIVAL_DEFAULT_HEADS;
}

/**
 * Whether a row prints the make-up at all.
 *
 * An arrival, OR the A Blancs line of a room that turns over — operator,
 * 2026-09-12: *« Repeat same info in a blancs as when they too arrive »*.
 *
 * That reverses a narrowing made an hour earlier, and the reversal is the
 * operator restating 0150's own reasoning: **the A Blancs block is the one read
 * in the morning, when the trolley is loaded.** Printing the make-up only in
 * Arrivées meant the person stripping 110 at 09:00 had to find it again in a
 * second block to learn it must come back as a twin — two places to look for
 * one job, on a page whose purpose is that there is only one.
 *
 * The duplication is therefore not redundancy. The two lines are read at
 * different hours by someone doing different work, and each has to stand alone.
 *
 * Everything else stays out: a recouche is refreshed, not remade, and an A
 * Blanc nobody follows into has no make-up to decide. Both would be ink on a
 * decision that does not exist.
 */
export function showMakeUp(row: Pick<CleaningRow, 'section' | 'turnover'>): boolean {
  if (row.section === 'arrivees') return true;
  return row.section === 'ablancs' && row.turnover;
}
